/**
 * Client-Side Rate Limiting Service
 *
 * Prevents spam by tracking user actions and enforcing cooldown periods.
 * Uses localStorage to persist rate limits across page reloads.
 *
 * NOTE: This is CLIENT-SIDE only. For production, you should also implement
 * server-side rate limiting in Firebase Functions or Firestore Security Rules.
 */

const RATE_LIMIT_STORAGE_KEY = 'coincommons_rate_limits';

// Rate limit configurations (in milliseconds)
const RATE_LIMITS = {
  // Comment & reply actions
  comment_create: {
    cooldown: 5000, // 5 seconds between comments
    maxPerHour: 30,  // Max 30 comments per hour
    message: 'Please wait between comments'
  },

  // Vote actions
  vote: {
    cooldown: 500,  // 0.5 second between votes
    maxPerHour: 100, // Max 100 votes per hour
    message: 'Please wait before voting again'
  },

  // Flag actions
  flag: {
    cooldown: 5000,  // 5 seconds between flags
    maxPerHour: 10,  // Max 10 flags per hour
    message: 'Please wait before report again'
  },

  // Alias creation
  alias_create: {
    cooldown: 1000,  // 1 seconds between alias changes
    maxPerHour: 50,  // Max 50 alias changes per hour
    message: 'Please wait before creating another alias'
  }
};

/**
 * Get rate limit data from localStorage
 */
const getRateLimitData = () => {
  try {
    const data = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error reading rate limit data:', error);
    return {};
  }
};

/**
 * Save rate limit data to localStorage
 */
const saveRateLimitData = (data) => {
  try {
    localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving rate limit data:', error);
  }
};

/**
 * Get user's action history for a specific action type
 */
const getUserActionHistory = (userAddress, actionType) => {
  const data = getRateLimitData();
  const key = `${userAddress}_${actionType}`;
  return data[key] || { timestamps: [] };
};

/**
 * Save user's action history
 */
const saveUserActionHistory = (userAddress, actionType, history) => {
  const data = getRateLimitData();
  const key = `${userAddress}_${actionType}`;
  data[key] = history;
  saveRateLimitData(data);
};

/**
 * Clean up old timestamps (older than 1 hour)
 */
const cleanupOldTimestamps = (timestamps) => {
  const oneHourAgo = Date.now() - (60 * 60 * 1000);
  return timestamps.filter(ts => ts > oneHourAgo);
};

/**
 * Check if action is allowed based on rate limits
 *
 * @param {string} userAddress - User's wallet address
 * @param {string} actionType - Type of action (comment_create, vote, flag, etc.)
 * @returns {{ allowed: boolean, message: string, retryAfter: number }}
 */
export const checkRateLimit = (userAddress, actionType) => {
  if (!userAddress || !actionType) {
    return { allowed: false, message: 'Invalid parameters', retryAfter: 0 };
  }

  const config = RATE_LIMITS[actionType];
  if (!config) {
    // No rate limit configured for this action, allow it
    return { allowed: true, message: '', retryAfter: 0 };
  }

  const history = getUserActionHistory(userAddress, actionType);
  const now = Date.now();

  // Clean up old timestamps
  history.timestamps = cleanupOldTimestamps(history.timestamps);

  // Check cooldown period
  if (history.timestamps.length > 0) {
    const lastActionTime = history.timestamps[history.timestamps.length - 1];
    const timeSinceLastAction = now - lastActionTime;

    if (timeSinceLastAction < config.cooldown) {
      const retryAfter = Math.ceil((config.cooldown - timeSinceLastAction) / 1000);
      return {
        allowed: false,
        message: `${config.message} (${retryAfter}s remaining)`,
        retryAfter
      };
    }
  }

  // Check hourly limit
  if (history.timestamps.length >= config.maxPerHour) {
    return {
      allowed: false,
      message: `Rate limit exceeded. Maximum ${config.maxPerHour} ${actionType}s per hour.`,
      retryAfter: 60 // Suggest retry after 1 minute
    };
  }

  return { allowed: true, message: '', retryAfter: 0 };
};

/**
 * Record an action after it's been performed
 *
 * @param {string} userAddress - User's wallet address
 * @param {string} actionType - Type of action (comment_create, vote, flag, etc.)
 */
export const recordAction = (userAddress, actionType) => {
  if (!userAddress || !actionType) return;

  const history = getUserActionHistory(userAddress, actionType);
  const now = Date.now();

  // Clean up old timestamps
  history.timestamps = cleanupOldTimestamps(history.timestamps);

  // Add new timestamp
  history.timestamps.push(now);

  // Save updated history
  saveUserActionHistory(userAddress, actionType, history);
};

/**
 * Clear rate limit history for a user (for testing)
 *
 * @param {string} userAddress - User's wallet address
 * @param {string} actionType - Optional specific action type to clear
 */
export const clearRateLimits = (userAddress, actionType = null) => {
  const data = getRateLimitData();

  if (actionType) {
    // Clear specific action type
    const key = `${userAddress}_${actionType}`;
    delete data[key];
  } else {
    // Clear all actions for this user
    Object.keys(data).forEach(key => {
      if (key.startsWith(userAddress)) {
        delete data[key];
      }
    });
  }

  saveRateLimitData(data);
};

/**
 * Get rate limit status for display
 *
 * @param {string} userAddress - User's wallet address
 * @param {string} actionType - Type of action
 * @returns {{ actionsInLastHour: number, maxPerHour: number, cooldownSeconds: number }}
 */
export const getRateLimitStatus = (userAddress, actionType) => {
  const config = RATE_LIMITS[actionType];
  if (!config) {
    return { actionsInLastHour: 0, maxPerHour: 0, cooldownSeconds: 0 };
  }

  const history = getUserActionHistory(userAddress, actionType);
  history.timestamps = cleanupOldTimestamps(history.timestamps);

  return {
    actionsInLastHour: history.timestamps.length,
    maxPerHour: config.maxPerHour,
    cooldownSeconds: config.cooldown / 1000
  };
};

/**
 * Clean up all expired rate limit data (run on app init)
 */
export const cleanupExpiredRateLimits = () => {
  try {
    const data = getRateLimitData();
    let cleaned = false;

    Object.keys(data).forEach(key => {
      const history = data[key];
      if (history && history.timestamps) {
        const cleanedTimestamps = cleanupOldTimestamps(history.timestamps);
        if (cleanedTimestamps.length === 0) {
          // No recent actions, remove this entry
          delete data[key];
          cleaned = true;
        } else if (cleanedTimestamps.length !== history.timestamps.length) {
          // Some timestamps were old, update
          data[key].timestamps = cleanedTimestamps;
          cleaned = true;
        }
      }
    });

    if (cleaned) {
      saveRateLimitData(data);
    }
  } catch (error) {
    console.error('Error cleaning up rate limits:', error);
  }
};
